/**
 * Auth storage — JWT lifecycle in `sessionStorage`.
 *
 * Per the data-layer handoff §5–§6, the token MUST live at the exact key
 * `portal_jwt`. This is locked in by the chatbot widget architecture: the
 * widget reads the token directly from `sessionStorage` and falls back to
 * its own PIN+DOB step-up if the key is missing.
 *
 * Don't:
 *   - Rename the key.
 *   - Store the token in `localStorage` (60-min TTL is too short to outlive
 *     a browser close, and the chatbot spec calls out sessionStorage
 *     specifically).
 *   - Rotate the JWT per request — issue once, refresh once. Rotating
 *     breaks chatbot session continuity even when the underlying claims
 *     are identical.
 *   - Verify the JWT signature in the browser. There's no JWKS endpoint.
 */
import type { AuthenticateResponse, MagicLinkResponse } from './types';
import { getTokenExpMs } from '../lib/jwt';

const TOKEN_KEY = 'portal_jwt';
const EXP_KEY = 'portal_jwt_exp';
const PATIENT_ID_KEY = 'portal_patient_id';
const TENANT_ID_KEY = 'portal_tenant_id';
const REDIRECT_PATH_KEY = 'portal_redirect_path';

/** All keys this app writes; clearAuth removes anything starting with `portal_`. */
export const PORTAL_STORAGE_PREFIX = 'portal_';

/**
 * Persist auth state from a successful login response.
 *
 * Accepts the union of PIN+DOB and magic-link response shapes — they
 * share the same four canonical fields (access_token, expires_in,
 * patient_id, tenant_id). The magic-link response also carries
 * `redirect_path`, which we persist for the soft-redirect-on-scope-
 * violation flow (see `api/client.ts` 401 branching for
 * APPOINTMENT_SCOPE_VIOLATION).
 *
 * Per decision ledger #1: the JWT key MUST stay at exactly
 * `portal_jwt`. The chatbot widget reads it from there.
 */
export function storeAuth(
  res: AuthenticateResponse | MagicLinkResponse,
): void {
  sessionStorage.setItem(TOKEN_KEY, res.access_token);
  // Use the JWT's own `exp` claim if it parses; fall back to expires_in. The
  // claim is authoritative — `expires_in` is what the *server* thinks, but
  // clock skew on the client makes the JWT-derived value safer.
  const expFromJwt = getTokenExpMs(res.access_token);
  const expFromBody = Date.now() + res.expires_in * 1000;
  const exp = expFromJwt > 0 ? expFromJwt : expFromBody;
  sessionStorage.setItem(EXP_KEY, String(exp));
  sessionStorage.setItem(PATIENT_ID_KEY, String(res.patient_id));
  sessionStorage.setItem(TENANT_ID_KEY, res.tenant_id);
  // Magic-link only: persist redirect_path for the scope-violation
  // soft-redirect flow. Absent on PIN+DOB responses (which is fine —
  // the field stays unset and the consumer treats absent as "no
  // remembered path").
  if ('redirect_path' in res && typeof res.redirect_path === 'string') {
    sessionStorage.setItem(REDIRECT_PATH_KEY, res.redirect_path);
  }
}

/**
 * Return a usable token, or null if missing / expired.
 *
 * "Pre-empt expiry by 10s" is per §5: if the token has less than 10s left,
 * treat it as already expired so we don't fire a request that the server
 * rejects mid-flight.
 */
export function getValidToken(): string | null {
  const token = sessionStorage.getItem(TOKEN_KEY);
  const exp = Number(sessionStorage.getItem(EXP_KEY) ?? 0);
  if (!token || exp - Date.now() < 10_000) return null;
  return token;
}

/** Read the (possibly expired) token without the validity check. */
export function readToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function readPatientId(): number | null {
  const v = sessionStorage.getItem(PATIENT_ID_KEY);
  if (!v) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function readTenantId(): string | null {
  return sessionStorage.getItem(TENANT_ID_KEY);
}

/**
 * Read the redirect_path stored at auth time, if any.
 *
 * Used by the 401 handler in `api/client.ts` when an appointment-scope
 * session attempts to act outside its scope. Soft-redirects to the
 * remembered path (the original appointment route) instead of
 * clearing the session.
 *
 * Returns null on PIN+DOB sessions (which never persist this field)
 * or if storage was cleared.
 */
export function readRedirectPath(): string | null {
  return sessionStorage.getItem(REDIRECT_PATH_KEY);
}

/** Clear all `portal_*` keys. Called on logout and on 401 from any API call. */
export function clearAuth(): void {
  for (const key of Object.keys(sessionStorage)) {
    if (key.startsWith(PORTAL_STORAGE_PREFIX)) {
      sessionStorage.removeItem(key);
    }
  }
}
